import { CartUpdatedEvent } from '@theme/events';

/**
 * Bloc « Comment allez-vous le poser ? » du panier.
 *
 * Deux gestes : ajouter une option (colle, poseur), ou remplacer un décor
 * classique par le même décor en adhésif. Le remplacement ajoute la nouvelle
 * ligne avant de retirer l'ancienne : si l'ajout échoue, le panier reste
 * exactement dans l'état où le client l'a laissé.
 */
class MsCartPose extends HTMLElement {
  #controller = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    for (const button of this.querySelectorAll('[data-ms-pose-action]')) {
      button.addEventListener('click', (event) => this.#onClick(event), { signal });
    }

    // Le jour de l'entretien ne peut pas être passé : au plus tôt demain.
    for (const input of this.querySelectorAll('[data-ms-min-tomorrow]')) {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      input.min = d.toISOString().slice(0, 10);
    }

    // Le lé de sécurité au prix exact : sa variante se prépare dès que le
    // panier s'affiche, pour qu'elle soit prête au moment du clic.
    for (const button of this.querySelectorAll('[data-ms-exact]')) this.#exactVariant(button).catch(() => {});
  }

  /** Variantes exactes déjà demandées, par cotes : une seule requête par lé. */
  static #exact = new Map();

  /**
   * La variante « Support · laize × hauteur cm » du décor, au prix exact,
   * créée (ou retrouvée) par le service sur mesure.
   * @param {HTMLElement} button
   * @returns {Promise<{id:number, fresh:boolean, at:number}>}
   */
  #exactVariant(button) {
    const { msExact, msProduct, msBase, msW, msH } = button.dataset;
    const key = `${msBase}:${msW}x${msH}`;
    const cached = MsCartPose.#exact.get(key);
    if (cached) return cached;
    const request = (async () => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 6000);
      try {
        const response = await fetch(new URL('/variant', msExact), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ product_id: Number(msProduct), variant_id: Number(msBase), width_cm: Number(msW), height_cm: Number(msH) }),
          signal: controller.signal,
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data.ok || !data.variant_id) throw new Error('exact');
        return { id: Number(data.variant_id), fresh: !data.reused, at: Date.now(), cents: Math.round(Number(data.price) * 100) };
      } finally {
        clearTimeout(timer);
      }
    })();
    MsCartPose.#exact.set(key, request);
    request.catch(() => MsCartPose.#exact.delete(key));
    return request;
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  get #status() {
    return this.querySelector('[data-ms-pose-status]');
  }

  /** Identifiant de la section à re-rendre, si le bloc vit dans le tiroir. */
  get #sectionId() {
    return this.closest('cart-drawer')?.dataset.sectionId ?? null;
  }

  /**
   * @param {string} url
   * @param {object} body
   * @returns {Promise<object>}
   */
  async #post(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
      signal: this.#controller?.signal,
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'Panier indisponible');
    return data;
  }

  /** @param {MouseEvent} event */
  async #onClick(event) {
    const button = event.currentTarget;
    if (!(button instanceof HTMLElement) || button.getAttribute('aria-busy') === 'true') return;

    event.preventDefault();
    this.#setBusy(button, true);
    if (this.#status) this.#status.textContent = '';

    try {
      const request = button.dataset.msPoseAction === 'swap' ? this.#swap(button) : this.#add(button);
      // La carte s'efface pendant que la requête part, et un mot « Ajouté »
      // se pose en haut du tiroir : le panier se met à jour derrière, sans
      // que la cliente regarde un chargement.
      const [state] = await Promise.all([request, this.#leave(button.closest('.ms-pose__card'))]);
      this.#toast(button.dataset.msLabel || 'Ajouté');
      this.#refresh(state);
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.#setBusy(button, false);
      if (this.#status) {
        this.#status.textContent = button.dataset.msNeeds && error.message === button.dataset.msNeeds
          ? error.message
          : "Nous n'avons pas pu mettre le panier à jour. Réessayez dans un instant.";
      }
    }
  }

  /**
   * Ajoute une option. Le lé de sécurité passe par là aussi : c'est le décor
   * lui-même, en quantité d'un lé, avec ses propriétés qui disent pourquoi.
   * @param {HTMLElement} button
   */
  async #add(button) {
    // La pose ne s'ajoute pas comme une option fixe : sa variante et sa
    // quantité dépendent de la surface des décors (assets/ms-pose.js).
    if (button.hasAttribute('data-ms-pose-sync') && window.MsPose) {
      const result = await window.MsPose.sync({ want: true, sections: this.#sectionId, sectionsUrl: window.location.pathname });
      if (!result.changed) throw new Error('pose');
      return { sections: result.sections, item_count: result.itemCount };
    }
    let fresh = null;
    if (button.dataset.msExact) {
      const exact = await this.#exactVariant(button);
      button.dataset.msVariant = String(exact.id);
      // Une variante neuve met quelques secondes à être visible en boutique.
      if (exact.fresh) fresh = exact;
    }
    const item = { id: Number(button.dataset.msVariant), quantity: Number(button.dataset.msQuantity) || 1 };
    if (button.dataset.msProps) {
      try {
        const props = JSON.parse(button.dataset.msProps);
        if (props && typeof props === 'object' && !Array.isArray(props)) item.properties = props;
      } catch {
        /* sans propriétés lisibles, l'article part quand même */
      }
    }

    // Les champs de la carte (jour, créneau) partent sur la commande. Un
    // champ requis vide arrête l'ajout et le dit, au lieu d'envoyer un
    // rendez-vous sans date.
    const card = button.closest('.ms-pose__card');
    for (const field of card?.querySelectorAll('[data-ms-field]') ?? []) {
      const wrap = field.closest('.ms-pose__field');
      if (field.required && !field.value) {
        wrap?.classList.add('is-missing');
        throw new Error(button.dataset.msNeeds || 'Complétez la carte avant d’ajouter.');
      }
      wrap?.classList.remove('is-missing');
      if (field.value) {
        item.properties = item.properties || {};
        item.properties[field.dataset.msField] = field.type === 'date' ? new Date(field.value).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : field.value;
      }
    }
    const body = { items: [item] };
    const sections = this.#sectionId;
    if (sections) Object.assign(body, { sections, sections_url: window.location.pathname });
    if (!fresh) return this.#post(Theme.routes.cart_add_url, body);

    // Variante créée à l'instant : on lui laisse 2 s, puis le panier peut la
    // refuser (422) le temps qu'elle apparaisse ; on réessaie, six fois au plus.
    const ready = fresh.at + 2000 - Date.now();
    if (ready > 0) await new Promise((resolve) => setTimeout(resolve, ready));
    // Visible ne veut pas dire au bon prix : ajoutée trop tôt, une variante neuve part à 0 € (vu le 28 oct. 2026).
    for (let i = 0; i < 10 && fresh.cents; i += 1) {
      try {
        const v = await (await fetch(`/variants/${fresh.id}.js?ms=${Date.now()}`, { headers: { Accept: 'application/json' } })).json();
        if (v?.price === fresh.cents) break;
      } catch { /* pas encore visible */ }
      await new Promise((resolve) => setTimeout(resolve, 900));
    }
    for (let attempt = 0; ; attempt += 1) {
      try {
        return await this.#post(Theme.routes.cart_add_url, body);
      } catch (error) {
        if (error.name === 'AbortError' || attempt >= 5) throw error;
        await new Promise((resolve) => setTimeout(resolve, 900));
      }
    }
  }

  /**
   * Remplace un décor par sa version adhésive : mêmes dimensions, même
   * cadrage, même surface facturée. Seul le support change.
   * @param {HTMLElement} button
   */
  async #swap(button) {
    const properties = this.#properties(button);
    const quantity = Number(button.dataset.msQuantity) || 1;

    await this.#post(Theme.routes.cart_add_url, {
      items: [{ id: Number(button.dataset.msVariant), quantity, properties }],
    });

    const body = { id: button.dataset.msKey, quantity: 0 };
    const sections = this.#sectionId;
    if (sections) Object.assign(body, { sections, sections_url: window.location.pathname });
    return this.#post(Theme.routes.cart_change_url, body);
  }

  /**
   * Les propriétés de la ligne d'origine, avec le support remis à jour.
   * @param {HTMLElement} button
   * @returns {Record<string, string>}
   */
  #properties(button) {
    const properties = {};

    let brut = null;
    try {
      brut = JSON.parse(button.dataset.msProps || '{}');
    } catch {
      brut = null;
    }

    // Liquid sérialise les propriétés d'une ligne en tableau de paires, pas en
    // objet. L'API du panier, elle, n'accepte qu'un objet.
    if (Array.isArray(brut)) {
      for (const paire of brut) {
        if (Array.isArray(paire) && paire.length === 2) properties[paire[0]] = paire[1];
      }
    } else if (brut && typeof brut === 'object') {
      Object.assign(properties, brut);
    }

    const support = button.dataset.msSupport;
    if (support && 'Support' in properties) properties.Support = support;
    return properties;
  }

  /** @param {object} state */
  #refresh(state) {
    if (!state.sections) {
      window.location.reload();
      return;
    }

    document.dispatchEvent(
      new CartUpdatedEvent(
        {},
        {
          itemCount: state.item_count,
          source: 'ms-cart-pose',
          sections: state.sections,
        },
      ),
    );
    this.#keepInView();
  }

  /**
   * Le tiroir se redessine après l'ajout, remonte en haut, et la nouvelle
   * ligne pousse le bloc vers le bas. On note où le bloc était à l'écran,
   * puis pendant une seconde et demie, à chaque image, on remet le tiroir
   * de sorte que le bloc revienne exactement à cette place : la personne
   * ne bouge pas, et prend la carte suivante.
   */
  #keepInView() {
    const drawer = this.closest('cart-drawer');
    if (!drawer) return;
    const scroller = MsCartPose.#scrollerOf(this);
    const before = scroller ? this.getBoundingClientRect().top - scroller.getBoundingClientRect().top : null;
    if (before === null) return;

    const start = performance.now();
    const tick = () => {
      const block = drawer.querySelector('ms-cart-pose');
      const sc = block ? MsCartPose.#scrollerOf(block) : null;
      if (block && sc) {
        const now = block.getBoundingClientRect().top - sc.getBoundingClientRect().top;
        const delta = now - before;
        if (Math.abs(delta) > 1) sc.scrollTop += delta;
      }
      if (performance.now() - start < 1500) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  /**
   * Le premier ancêtre qui défile réellement.
   * @param {Element} el
   * @returns {Element|null}
   */
  static #scrollerOf(el) {
    let node = el.parentElement;
    while (node) {
      const style = getComputedStyle(node);
      if (/(auto|scroll)/.test(style.overflowY) && node.scrollHeight > node.clientHeight + 1) return node;
      node = node.parentElement;
    }
    return document.scrollingElement;
  }

  /**
   * La carte s'efface : légère montée, fondu, puis elle se referme pour que
   * les voisines glissent à sa place. Résolu quand c'est fini (ou tout de
   * suite si la personne a demandé moins de mouvement).
   * @param {HTMLElement|null} card
   * @returns {Promise<void>}
   */
  #leave(card) {
    if (!card || !card.animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return Promise.resolve();
    const width = card.getBoundingClientRect().width;
    const fade = card.animate(
      [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(-14px) scale(0.96)' }],
      { duration: 260, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'forwards' },
    );
    return fade.finished.then(() => {
      const close = card.animate(
        [{ flexBasis: `${width}px`, marginInlineEnd: '0px' }, { flexBasis: '0px', marginInlineEnd: '-10px' }],
        { duration: 220, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'forwards' },
      );
      card.style.overflow = 'hidden';
      card.style.padding = '0';
      card.style.borderWidth = '0';
      return close.finished.then(() => undefined);
    }).catch(() => undefined);
  }

  /**
   * Un mot posé en haut du tiroir, une seconde et demie, puis il s'efface.
   * @param {string} label
   */
  #toast(label) {
    const old = document.querySelector('.ms-toast');
    if (old) old.remove();
    const toast = document.createElement('div');
    toast.className = 'ms-toast';
    toast.setAttribute('role', 'status');
    toast.innerHTML = '<span class="ms-toast__check" aria-hidden="true"></span><span class="ms-toast__text"></span>';
    toast.querySelector('.ms-toast__text').textContent = `${label} · ajouté`;
    document.body.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('is-in'));
    setTimeout(() => {
      toast.classList.remove('is-in');
      setTimeout(() => toast.remove(), 400);
    }, 1600);
  }

  /**
   * @param {HTMLElement} button
   * @param {boolean} busy
   */
  #setBusy(button, busy) {
    button.setAttribute('aria-busy', String(busy));
    for (const other of this.querySelectorAll('[data-ms-pose-action]')) {
      if (other !== button) other.toggleAttribute('disabled', busy);
    }
  }
}

if (!customElements.get('ms-cart-pose')) {
  customElements.define('ms-cart-pose', MsCartPose);
}
